import { captureDatadogRumAction } from "../../telemetry/datadog-rum";
import { matchRoute, type RouteName } from "@softmax/web-addresses";

export type NavigationStage = "shell" | "content" | "personal";
export type Navigation =
  | "initial_load"
  | "reload"
  | "push"
  | "replace"
  | "traverse"
  | "restore";
type Status = "started" | "ready" | "error" | "timeout" | "abandoned";
const pageStages = ["shell", "content"] as const;
const personalStages = [...pageStages, "personal"] as const;
// Each family has markers at its usable content, including empty and failed states.
export const adopted: Partial<Record<RouteName, readonly NavigationStage[]>> = {
  docs: pageStages,
  browse: personalStages,
  play: pageStages,
  "game.play": personalStages,
  account: pageStages,
  policy: pageStages,
  policyVersion: pageStages,
  coworlds: pageStages,
  coworldManage: pageStages,
  episode: pageStages,
};
export const budgets = {
  cold: { shell: 2500, content: 2500, personal: 2500 },
  soft: { shell: 200, content: 2000, personal: 2000 },
};
export type Visit = {
  id: string;
  path: string;
  search: string;
  sourcePath: string;
  sourceSearch: string;
  family: RouteName;
  stages: readonly NavigationStage[];
  started: number;
  navigation: Navigation;
  load: "cold" | "soft" | "restore";
  foreground: boolean;
  redirected: boolean;
  settled: Set<NavigationStage>;
  overdue: Set<NavigationStage>;
};

export function report({
  current,
  stage,
  status,
  release,
}: {
  readonly current: Visit;
  readonly stage: NavigationStage;
  readonly status: Status;
  readonly release: string;
}) {
  const detail = {
    schema_version: 1,
    release,
    navigation_id: current.id,
    route_family: current.family,
    navigation: current.navigation,
    load: current.load,
    stage,
    status,
    elapsed_ms: current.foreground
      ? Math.max(0, performance.now() - current.started)
      : null,
    budget_ms: budgets[current.load === "cold" ? "cold" : "soft"][stage],
    foreground: current.foreground,
    redirected: current.redirected,
  };
  // The same marks are readable in local browser proofs without a RUM token.
  performance.mark(`navigation_readiness:${stage}:${status}`, { detail });
  captureDatadogRumAction({ name: "navigation_readiness", context: detail });
}

export function pathsMatch({
  left,
  right,
}: {
  readonly left: string;
  readonly right: string;
}) {
  if (left === right) return true;

  const route = matchRoute(left);
  // Different percent encodings can address the same decoded route parameters.
  return (
    route.name !== "unknown" &&
    JSON.stringify(route) === JSON.stringify(matchRoute(right))
  );
}

export function readinessFamily(name: ReturnType<typeof matchRoute>["name"]) {
  if (name === "legacyPlay") return "game.play";

  if (name === "accountAdvanced") return "account";

  return name;
}

export type NavigationStart = {
  readonly url: string;
  readonly navigation: Navigation;
  readonly started?: number | undefined;
  readonly load?: Visit["load"] | undefined;
  readonly redirected?: boolean | undefined;
};
